import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Grid, Spinner } from '@factorialco/deprecated-design-system'
import { WorkingConditionVersionApplicationFragment } from 'generated/resources/graphql'
import { observable } from 'mobx'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity, employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'

import { Incidences } from 'modules/bookkeepers/store/collections/incidences'
import type Incidence from 'modules/bookkeepers/store/models/Incidence'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import useContracts from 'modules/contracts/hooks/useContracts'
import useFetchContractHistoryData from 'modules/contracts/hooks/useFetchContractHistoryData'
import { useFetchContractsPermissions } from 'modules/contracts/hooks/useFetchContractsPermissions'
import { useFetchCustomFields } from 'modules/contracts/hooks/useFetchCustomFields'
import { useFetchIncidenceRules } from 'modules/contracts/hooks/useFetchIncidenceRules'
import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import { ContractHistoryDataProvider } from 'modules/contracts/lib/contractHistoryDataContext'
import { sortContractVersions } from 'modules/contracts/lib/contractSchedule'
import { useContractVersions } from 'modules/contracts/lib/contractVersionsContext'
import { ApprovalStatus } from 'modules/contracts/lib/enums'
import { LegalEntityContractDataProvider } from 'modules/contracts/lib/legalEntityContractDataContext'
import { Compensations } from 'modules/contracts/store/collections/compensations'
import { ContractVersionRequests } from 'modules/contracts/store/collections/contractVersionRequests'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import AboutTheRequest from './AboutTheRequest'
import Compensation from './Compensation'
import ContractInformation from './ContractInformation'
import Header from './Header'
import History from './History'
import JobPosition from './JobPosition'
import LegalAndLabour from './LegalAndLabour'
import ModalSelector from './ModalSelector'
import OtherContractInformation from './OtherContractInformation'
import Time from './Time'

interface Props {
  employee: Employee
  selectedContractVersion: ContractVersion
  setSelectedContractVersion: (contractVersion: ContractVersion | undefined) => void
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
}

const Loading = () => {
  return (
    <Box width="full" height="full" justifyContent="center" alignItems="center">
      <Spinner />
    </Box>
  )
}

const ContractDetails = ({
  employee,
  selectedContractVersion,
  setSelectedContractVersion,
  workingConditionVersionApplication,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showModal, setShowModal] = React.useState(false)
  const [showHistory, setShowHistory] = React.useState(false)
  const [hiringIncidence, setHiringIncidence] = React.useState<Incidence>()
  const [option, setOption] = React.useState(FLOWS.promotion)
  const contractVersions = useContractVersions()
  const contractVersionId = selectedContractVersion.id
  const employeeId = employee.id

  const contractVersionRequests = useLazyRef(() => new ContractVersionRequests()).current
  const canSeeAdditionalCompensations = policyAuthorizations.for(
    'contracts.see_additional_compensation',
    employee
  )
  const canSeeContractsExtraInformation = policyAuthorizations.for(
    'contracts.see_contract_extra_info',
    employee
  )
  const professionalsActivated = hasFeature('factorial_professionals')
  const legalEntity = employeeLegalEntity(employee)
  const legalEntityId = employeeLegalEntityId(employee)
  const {
    loading: loadingLEData,
    spanishContractData,
    frenchContractData,
    germanContractData,
    portugueseContractData,
    commonContractData,
  } = useFetchLegalEntityContractData(legalEntityId, legalEntity.country)

  const compensations = useLazyRef(() => new Compensations()).current
  const incidences = useLazyRef(() => new Incidences()).current
  const listOfFields = useLazyRef(() => observable.map(new Map())).current

  const {
    loading: loadingCustomFields,
    resourceFields,
    valuesCollection,
  } = useFetchCustomFields({
    fieldableId: legalEntityId,
    contractVersion: selectedContractVersion,
    employee,
  })
  const { loading: loadingPermissions } = useFetchContractsPermissions(employee)

  const { loading } = useAsyncData(async () => {
    if (loadingPermissions) return Promise.resolve()
    const promises = [
      contractVersionRequests.fetchWithApprovalStatusByEmployees(
        [employeeId],
        [ApprovalStatus.pending]
      ),
    ]
    if (canSeeAdditionalCompensations) {
      promises.push(
        compensations.fetch({
          data: {
            contract_version_ids: [contractVersionId],
          },
        })
      )
    }
    if (professionalsActivated) {
      promises.push(
        incidences.fetch({
          data: {
            employee_ids: [employeeId],
            type: [INCIDENCE_TYPES.hiring],
            status: [
              INCIDENCE_STATUSES.in_preparation,
              INCIDENCE_STATUSES.to_do,
              INCIDENCE_STATUSES.doing,
            ],
          },
        })
      )
    }
    await Promise.all(promises)
    setHiringIncidence(incidences.last())
  }, [contractVersionId, loadingPermissions])

  const allContractVersionsSorted = sortContractVersions(contractVersions.models)
  const contractVersionIds = contractVersions.map((cv) => cv.id)
  const { additionalCompensations, customFieldValues, newAdditionalCompensations } =
    useFetchContractHistoryData(contractVersionIds)

  const { contracts } = useContracts(selectedContractVersion, allContractVersionsSorted)
  const { incidenceRules } = useFetchIncidenceRules(hiringIncidence)

  if (loadingPermissions || loadingLEData || loading) return <Loading />

  const contractVersionRequest = contractVersionRequests.models[0]

  const onClickFix = () => {
    if (!contractVersionRequest) {
      navigate(
        navigation.employees.id.contractVersions.fix.fullPath({
          id: employeeId.toString(),
          contractVersionId: selectedContractVersion.id.toString(),
        })
      )
    } else {
      setShowModal(true)
    }
  }

  const onClickDelete = () => {
    if (contractVersionRequest) {
      setShowModal(true)
    }
  }

  const onCloseModal = () => setShowModal(false)

  const onClickEdit = () => setShowModal(true)

  const toggleHistory = () => {
    if (!showHistory) track(trackingEvents.employee.contracts.startContractHistory)
    setShowHistory(!showHistory)
  }

  const isItalianLegalEntity =
    legalEntity.isItalianLegalEntity && selectedContractVersion.hasForfaitJours

  const showLegalAndLabour =
    canSeeContractsExtraInformation &&
    (selectedContractVersion.isFrench ||
      selectedContractVersion.isSpanish ||
      isItalianLegalEntity ||
      selectedContractVersion.isGerman)

  const hasCues = (professionalsActivated && hiringIncidence && incidenceRules?.length > 0) || false

  return (
    <ContractCuesProvider
      collection={{
        hasCues: hasCues,
        incidenceRules: incidenceRules,
        listOfFields: listOfFields,
      }}
    >
      <LegalEntityContractDataProvider
        spanishContractData={spanishContractData}
        frenchContractData={frenchContractData}
        germanContractData={germanContractData}
        portugueseContractData={portugueseContractData}
        commonContractData={commonContractData}
      >
        <ContractHistoryDataProvider
          additionalCompensations={additionalCompensations}
          customFieldValues={customFieldValues}
          newAdditionalCompensations={newAdditionalCompensations}
        >
          <Grid variant="fixed">
            <Box
              style={{
                position: 'relative',
                gridColumnStart: '2',
                gridColumnEnd: 'span 10',
              }}
              paddingY="s48"
              gridColSpan={{ xxs: 10 }}
              gap="s40"
            >
              <Header
                selectedContractVersion={selectedContractVersion}
                allContractVersionsSorted={allContractVersionsSorted}
                contractVersionRequest={contractVersionRequest}
                workingConditionVersionApplication={workingConditionVersionApplication}
                contracts={contracts}
                onClickEdit={onClickEdit}
                onClickFix={onClickFix}
                onClickDelete={onClickDelete}
                toggleHistory={toggleHistory}
              />
              <Box gap="s32">
                <ContractInformation
                  contractVersion={selectedContractVersion}
                  employee={employee}
                />
                <JobPosition contractVersion={selectedContractVersion} />
                <Compensation
                  contractVersion={selectedContractVersion}
                  employee={employee}
                  compensations={compensations.models}
                />
                <Time
                  contractVersion={selectedContractVersion}
                  employee={employee}
                  workingConditionVersionApplication={workingConditionVersionApplication}
                />
                {showLegalAndLabour && (
                  <LegalAndLabour
                    contractVersion={selectedContractVersion}
                    employee={employee}
                    isItalianLegalEntity={isItalianLegalEntity}
                  />
                )}
                <OtherContractInformation
                  loading={loadingCustomFields}
                  contractVersion={selectedContractVersion}
                  employee={employee}
                  customFields={resourceFields}
                  customValues={valuesCollection}
                />
                {contractVersionRequest && (
                  <AboutTheRequest
                    employee={employee}
                    contractVersionRequest={contractVersionRequest}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <History
            open={showHistory}
            selectedContractVersion={selectedContractVersion}
            allContractVersionsSorted={allContractVersionsSorted}
            contracts={contracts}
            setSelectedContractVersion={setSelectedContractVersion}
            toggleHistory={toggleHistory}
          />
          {showModal && (
            <ModalSelector
              onCloseModal={onCloseModal}
              selectedContractVersion={selectedContractVersion}
              employee={employee}
              allContractVersions={contractVersions}
              setOption={setOption}
              option={option}
              showFixOption={false}
              contractVersionRequest={contractVersionRequest}
            />
          )}
        </ContractHistoryDataProvider>
      </LegalEntityContractDataProvider>
    </ContractCuesProvider>
  )
}

export default observer(ContractDetails)
